import { Award, Calendar, MapPin, Building2, Target, Heart } from "lucide-react";
import CTAButtons from "@/components/CTAButtons";
import TrustBar from "@/components/TrustBar";

const VALUES = [
    {
        icon: Target,
        title: "Quality first",
        text: "Every workpaper, every reconciliation, every disclosure note is reviewed by a CA(SA). Our IRBA inspections speak for themselves.",
    },
    {
        icon: Heart,
        title: "Genuinely care",
        text: "Our brochure puts it plainly: we are passionate and we add value. We treat your business like our own.",
    },
    {
        icon: Award,
        title: "Modern and accredited",
        text: "Xero Gold Partner. SAICA member. IRBA registered. Member of Chartered Accountants Worldwide. The credentials matter.",
    },
];

export default function About() {
    return (
        <div data-testid="page-about">
            {/* Hero */}
            <section className="relative overflow-hidden bg-white border-b border-tca-line">
                <div className="absolute inset-0 grid-bg opacity-70" />
                <div className="container-tca py-20 md:py-28 relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-8 animate-fade-up">
                        <p className="overline text-tca-orange mb-4">About TC Auditors</p>
                        <h1 className="font-display font-extrabold text-tca-ink text-5xl sm:text-6xl lg:text-7xl leading-[0.98]">
                            A registered audit firm
                            <span className="block text-tca-orange">built in 2015.</span>
                        </h1>
                        <p className="mt-6 text-tca-mute text-lg leading-relaxed max-w-2xl">
                            TC Auditors (Pty) Ltd is a Chartered Accountants and Auditors practice founded in 2015 and based in Meyerton, Gauteng. We serve owner-managed businesses across South Africa with audit, tax, accounting, payroll and advisory work.
                        </p>
                    </div>
                    <div className="lg:col-span-4 grid grid-cols-2 gap-px bg-tca-line border border-tca-line">
                        {[
                            { icon: Calendar, label: "Founded", value: "2015" },
                            { icon: Building2, label: "Reg. No.", value: "2015/290284/07" },
                            { icon: MapPin, label: "Office", value: "Meyerton, Gauteng" },
                            { icon: Award, label: "Status", value: "Xero Gold Partner", logo: "/assets/xero-gold-partner-badge.png" },
                        ].map((b) => {
                            const Icon = b.icon;
                            return (
                                <div key={b.label} className="bg-white p-2 md:p-3 flex flex-col" data-testid={`about-fact-${b.label.toLowerCase().replace(/\s|\./g, "-")}`}>
                                    <Icon className="w-4 h-4 text-tca-orange mb-3" />
                                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-tca-mute mb-1">{b.label}</p>
                                    <p className="font-display font-bold text-tca-ink leading-tight">{b.value}</p>
                                    {b.logo && (
                                        <img src={b.logo} alt="Xero Gold Partner" className="mt-3 h-[88px] w-auto object-contain self-start" loading="lazy" />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Story */}
            <section className="bg-white">
                <div className="container-tca py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14">
                    <div className="lg:col-span-5">
                        <p className="overline mb-4">Our story</p>
                        <h2 className="font-display font-extrabold text-tca-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                            Ten years of building a quietly excellent practice.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-tca-mute leading-relaxed">
                        <p>
                            TC Auditors was registered with CIPC on 5 November 2015. Over the decade since, we have grown from a one-Director operation in Meyerton into a modern, cloud-first audit and accounting practice with three dedicated phone lines and a national footprint of clients.
                        </p>
                        <p>
                            We are <strong className="text-tca-ink">Chartered Accountants (SA)</strong> and <strong className="text-tca-ink">Registered Auditors</strong>. Audits and Independent Reviews are signed off in line with the International Standards on Auditing and ISRE 2400. Annual Financial Statements follow IFRS or IFRS for SMEs as the Public Interest Score requires.
                        </p>
                        <p>
                            On the accounting side we are an active <strong className="text-tca-ink">Xero Gold Partner</strong>. Cloud accounting is not a buzzword for us — every TCA client is run on Xero, with bank feeds, live management reports and a Xero App Store integration plan tailored to their business.
                        </p>
                        <p>
                            Our brochure puts our ambition plainly: <em>"Our ambition is to deliver the highest quality client service."</em> We mean it. Phone lines, WhatsApp groups, Zoom meetings, response times — we measure ourselves on them every month.
                        </p>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="bg-tca-fog border-y border-tca-line">
                <div className="container-tca py-20 md:py-28">
                    <div className="max-w-2xl mb-14">
                        <p className="overline mb-3">Our values</p>
                        <h2 className="font-display font-extrabold text-tca-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                            Three things we never compromise on.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-tca-line border border-tca-line">
                        {VALUES.map((v, i) => {
                            const Icon = v.icon;
                            return (
                                <div key={v.title} className="bg-white p-9 lift" data-testid={`value-${i}`}>
                                    <Icon className="w-7 h-7 text-tca-orange mb-6" />
                                    <h3 className="font-display font-bold text-2xl text-tca-ink mb-3">{v.title}</h3>
                                    <p className="text-tca-mute leading-relaxed">{v.text}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <TrustBar />

            {/* CTA */}
            <section className="bg-white">
                <div className="container-tca py-20 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-tca-ink leading-[1.05]">Bring us your toughest audit.</h2>
                        <p className="mt-4 text-tca-mute">We have nine packages and a small, motivated team. There is room for you.</p>
                    </div>
                    <CTAButtons testIdPrefix="about-cta" />
                </div>
            </section>
        </div>
    );
}
